import {
  type ScaleLinear,
  type ScaleTime,
  scaleLinear,
  scaleTime,
} from 'd3-scale';
import { useMemo } from 'react';

import { type Unit, format } from '@dynatrace-sdk/units';

import type { Formatter } from '../../../../../charts/core/types/formatter.js';
import type { MinMax } from '../../../../../charts/core/types/min-max.js';
import type { DataTableV2GanttColumnConfig } from '../../ColumnTypes/ColumnTypes.js';

type Scale =
  | ScaleTime<number, number, never>
  | ScaleLinear<number, number, never>;

export const useScaleAndFormatter = (
  width: number,
  config: DataTableV2GanttColumnConfig,
  min: MinMax['min'],
  max: MinMax['max'],
): {
  scale: Scale;
  formatter?: Formatter;
} => {
  const { xAxisType = 'numerical' } = config;
  const scale = useMemo(
    () =>
      xAxisType === 'time'
        ? scaleTime().domain([min, max]).range([0, width])
        : scaleLinear().domain([min, max]).range([0, width]),
    [max, min, width, xAxisType],
  );

  const formatter = useMemo<Formatter | undefined>(
    () =>
      config.formatter
        ? (value, unit: Unit | string | undefined) =>
            format(value, {
              input: unit as Unit,
              ...config.formatter,
            })
        : undefined,
    [config.formatter],
  );

  return useMemo(() => ({ scale, formatter }), [formatter, scale]);
};
